import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LedgerEvent, listLedgerEvents } from "../../api";
import { APP_ROUTES } from "../../routes";

interface EgressEntry {
  when: string;
  what: string;
  destination: string;
  size: string;
  sig: string;
}

function humanBytes(n?: number | null): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function liveToEntry(e: LedgerEvent): EgressEntry {
  return {
    when: e.created_at
      ? new Date(e.created_at).toLocaleString(undefined, {
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—",
    what: e.action,
    destination: e.destination ?? "—",
    size: humanBytes(e.payload_bytes),
    sig: e.entry_hash.slice(0, 6) + "…" + e.entry_hash.slice(-2),
  };
}

/**
 * Sample entries shown until the on-device egress ledger ships with the
 * Local Mind pilot. Every row models the real schema: what left the home,
 * where it went, how big it was, and the signature that proves the record.
 */
const SAMPLE_WEEK: EgressEntry[] = [
  {
    when: "Tue 07:52",
    what: "Fall-risk notice — “Margaret steadied herself at the kitchen counter”",
    destination: "Liz (daughter)",
    size: "412 B",
    sig: "a3f1…c9",
  },
  {
    when: "Tue 09:14",
    what: "GP appointment request (Thursday, knee)",
    destination: "gp-surgery.nhs.uk",
    size: "2.1 KB",
    sig: "77bd…04",
  },
  {
    when: "Wed 03:02",
    what: "Pharmacy repeat-prescription form (approved Tue 8:05am)",
    destination: "pharmacy portal",
    size: "3.4 KB",
    sig: "e2ea…5f",
  },
  {
    when: "Thu 18:30",
    what: "Weekly wellbeing summary — text only, no images",
    destination: "family group",
    size: "1.8 KB",
    sig: "09c4…b7",
  },
  {
    when: "Fri 07:48",
    what: "Morning note — “seemed a bit slower today, nothing urgent”",
    destination: "Liz (daughter)",
    size: "366 B",
    sig: "5d20…31",
  },
];

export default function EgressLedger() {
  const { id: projectId = "" } = useParams<{ id: string }>();
  const [live, setLive] = useState<LedgerEvent[] | null>(null);

  useEffect(() => {
    if (!projectId) return;
    listLedgerEvents(projectId, "egress")
      .then(setLive)
      .catch(() => setLive(null));
  }, [projectId]);

  const isLive = live !== null && live.length > 0;
  const entries: EgressEntry[] = isLive ? live.map(liveToEntry) : SAMPLE_WEEK;
  const totalBytes = isLive
    ? humanBytes(live.reduce((sum, e) => sum + (e.payload_bytes ?? 0), 0))
    : "8.1 KB";

  return (
    <div className="manifold-page">
      <header className="manifold-page-header">
        <p className="sg-section-kicker">Privacy · Local Mind</p>
        <h1>Egress Ledger — Family view</h1>
        <p className="text-muted">
          Everything that left the home this week, itemised, signed, and
          readable without a manual. Perception runs on the robot; raw
          video and audio never appear here because they never leave.
          If a byte isn&rsquo;t in this list, it didn&rsquo;t go anywhere.
        </p>
      </header>

      {isLive ? (
        <div className="chain-banner ok">
          <strong>✓ Live ledger</strong> — every entry below is
          Ed25519-signed and hash-chained; verify the full chain from the
          Compliance Ledger page.
        </div>
      ) : (
        <div className="sample-banner">
          <strong>Sample data.</strong> The on-device egress ledger ships
          with the Local Mind pilot — this preview shows exactly what a
          family will see, in the real schema. Live entries appear here
          the moment the project sends anything off-site.
        </div>
      )}

      <div className="stat-row">
        <div className="stat-card">
          <div className="stat-value">7</div>
          <div className="stat-label">days covered</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{entries.length}</div>
          <div className="stat-label">items left the home</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalBytes}</div>
          <div className="stat-label">total egress</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">0</div>
          <div className="stat-label">photos · video · audio</div>
        </div>
      </div>

      <div className="egress-list">
        {entries.map((e) => (
          <article className="egress-row" key={e.sig}>
            <time>{e.when}</time>
            <div>
              {e.what}
              <div className="egress-dest">→ {e.destination}</div>
            </div>
            <span className="egress-size">{e.size}</span>
            <span className="egress-sig">✓ signed {e.sig}</span>
          </article>
        ))}
        <p className="egress-none">
          {isLive ? (
            <>
              This is every third-party call our software made in the period,
              each signed with a digest of the exact bytes sent. It covers what
              we send &mdash; not any separate connection your robot&rsquo;s
              manufacturer may hold, which is a boundary we do not control and
              do not claim to.
            </>
          ) : (
            <>
              Sample data. Once a deployment runs, this list is populated from
              signed egress records rather than illustrations.
            </>
          )}
        </p>
      </div>

      <div className="egress-actions">
        <Link to={APP_ROUTES.project(projectId)} className="sg-btn sg-btn-secondary">
          Back to project
        </Link>
        <Link
          to={APP_ROUTES.compliance(projectId)}
          className="sg-btn sg-btn-ghost"
        >
          Full compliance ledger
        </Link>
      </div>
    </div>
  );
}
